import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

import {
  PUSTE_LICZNIKI,
  STATUSY_MODULU_AKTYWNE,
  alertyZOdpowiedzi,
  type SuroweLiczniki,
} from './panelGlowny';

/**
 * Liczniki Panelu głównego — z TYCH SAMYCH kartotek, które pokazują ekrany
 * „Aplikacje i urządzenia”, „Lokalizacje”, „Pracownicy”, „Użytkownicy”
 * i alerty NeuroSense. Dzięki temu liczba na karcie zgadza się z listą po
 * kliknięciu.
 *
 * Do 13.09.2026 hook czytał `device_pairings` (martwa dla najemcy — pisze do
 * niej tylko ekran super-admina) i `neurosense_alerts` (0 wierszy, nic do niej
 * nie pisze), a „delta vs ub. miesiąc” liczyła dzisiejsze wiersze po
 * `created_at`, więc mogła pokazać wyłącznie wzrost. OGYY widziało 0 urządzeń
 * przy 18 i 0 alertów przy 5. Szczegóły i decyzje:
 * docs/02-ARCHITECTURE/PANEL_GLOWNY_PRAWDZIWE_DANE.md
 *
 * Każde zapytanie ma własny try/catch: brak jednej tabeli nie ma zerować
 * całego panelu, ale błąd trafia do konsoli, a nie w ciszę — zero
 * nierozróżnialne od „nie udało się” to dokładnie usterka, którą naprawiamy.
 */
export function useDashboardStats() {
  const { currentOrganization } = useAuth();
  const [liczniki, setLiczniki] = useState<SuroweLiczniki>(PUSTE_LICZNIKI);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!currentOrganization) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setIsLoading(false);
        return;
      }

      const orgId = currentOrganization.organization_id;
      const licz = async (
        nazwa: string,
        zapytanie: PromiseLike<{ count: number | null; error: unknown }>
      ): Promise<number> => {
        try {
          const { count, error: err } = await zapytanie;
          if (err) throw err;
          return count ?? 0;
        } catch (err) {
          console.error(`Panel główny: licznik „${nazwa}” nie odpowiedział`, err);
          return 0;
        }
      };

      const [
        czujniki,
        licencjeUrzadzen,
        lokalizacjeAktywne,
        lokalizacjeWszystkie,
        pracownicyAktywni,
        kontaPortalu,
        modulyAktywne,
        alertyOdpowiedz,
      ] = await Promise.all([
        licz(
          'czujniki',
          supabase
            .from('neurosense_sensor_latest')
            .select('device_id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
        ),
        licz(
          'licencje urządzeń',
          supabase
            .from('device_licenses')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .eq('status', 'active')
        ),
        licz(
          'lokalizacje aktywne',
          supabase
            .from('organization_sites')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .eq('is_active', true)
        ),
        licz(
          'lokalizacje wszystkie',
          supabase
            .from('organization_sites')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
        ),
        licz(
          'pracownicy',
          supabase
            .from('shop_floor_workers')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .eq('is_active', true)
        ),
        licz(
          'konta w portalu',
          supabase
            .from('user_organizations')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .eq('status', 'active')
        ),
        licz(
          'moduły',
          supabase
            .from('module_subscriptions')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId)
            .in('status', [...STATUSY_MODULU_AKTYWNE])
        ),
        supabase
          .rpc('panel_glowny_alerty', { p_organization_id: orgId })
          .then(({ data, error: err }) => {
            if (err) {
              console.error('Panel główny: alerty nie odpowiedziały', err);
              return [] as unknown[];
            }
            return (data ?? []) as unknown[];
          }),
      ]);

      setLiczniki({
        czujniki,
        licencjeUrzadzen,
        lokalizacjeAktywne,
        lokalizacjeWszystkie,
        pracownicyAktywni,
        kontaPortalu,
        modulyAktywne,
        alerty: alertyZOdpowiedzi(alertyOdpowiedz),
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Nie udało się wczytać liczników panelu');
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    liczniki,
    isLoading,
    error,
    refetch: fetchStats,
  };
}
